import { useSyncExternalStore } from "react";
import { SURFACES } from "../render3d/materials/catalog3d";
import { finishSurfaceFor, type FinishOption } from "./catalog";
import { SWATCH_TEXTURES } from "./swatches.generated";

/**
 * Picker tiles: a finish's scanned swatch, tinted with the same math the 3D
 * material uses (`engine/materials.ts`), so the tile looks like the render.
 * Each swatch decodes once and each (finish, color) tints once. Until then,
 * and in jsdom (no canvas), callers show the flat color.
 */

const TILE_PX = 96;
const MAX_TILES = 240;

interface Source {
  data: Uint8ClampedArray;
  tint: "detail" | "colored";
  mean: readonly [number, number, number];
}

const decoded = new Map<string, Source | null>();
const loading = new Set<string>();
const tinted = new Map<string, string>();
let version = 0;
const listeners = new Set<() => void>();

const LINEAR = new Float32Array(256);
for (let i = 0; i < 256; i++) {
  const c = i / 255;
  LINEAR[i] = c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function toByte(linear: number): number {
  const v = Math.min(1, Math.max(0, linear));
  return Math.round(255 * (v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055));
}

function hexLinear(hex: string): [number, number, number] {
  const n = parseInt(hex.slice(1, 7), 16);
  return [LINEAR[(n >> 16) & 255], LINEAR[(n >> 8) & 255], LINEAR[n & 255]];
}

function canPaint(): boolean {
  return typeof document !== "undefined" && typeof navigator !== "undefined" && !/jsdom/i.test(navigator.userAgent);
}

function emit() {
  version++;
  for (const l of listeners) l();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

function load(set: string): void {
  if (decoded.has(set) || loading.has(set) || !canPaint()) return;
  const info = SWATCH_TEXTURES[set];
  if (!info) {
    decoded.set(set, null);
    return;
  }
  loading.add(set);
  const img = new Image();
  img.decoding = "async";
  img.onload = () => {
    loading.delete(set);
    const canvas = document.createElement("canvas");
    canvas.width = TILE_PX;
    canvas.height = TILE_PX;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) {
      decoded.set(set, null);
      return;
    }
    ctx.drawImage(img, 0, 0, TILE_PX, TILE_PX);
    decoded.set(set, { data: ctx.getImageData(0, 0, TILE_PX, TILE_PX).data, tint: info.tint, mean: info.mean ?? [0.5, 0.5, 0.5] });
    emit();
  };
  img.onerror = () => {
    loading.delete(set);
    decoded.set(set, null);
  };
  img.src = info.url;
}

function paint(src: Source, option: FinishOption, color: string): string | null {
  const def = SURFACES[finishSurfaceFor(option, color)];
  const canvas = document.createElement("canvas");
  canvas.width = TILE_PX;
  canvas.height = TILE_PX;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  const out = ctx.createImageData(TILE_PX, TILE_PX);
  const [tr, tg, tb] = hexLinear(color);
  const { data, mean } = src;
  const detail = def.detailStrength ?? 1;
  const k = def.tintStrength ?? 0;
  const meanLum = Math.max(0.2126 * mean[0] + 0.7152 * mean[1] + 0.0722 * mean[2], 1e-3);
  const ratio = [1 + (tr / Math.max(mean[0], 1e-3) - 1) * k, 1 + (tg / Math.max(mean[1], 1e-3) - 1) * k, 1 + (tb / Math.max(mean[2], 1e-3) - 1) * k];
  for (let i = 0; i < data.length; i += 4) {
    let r: number;
    let g: number;
    let b: number;
    if (src.tint === "detail") {
      const d = 1 + ((2 * data[i]) / 255 - 1) * detail;
      r = tr * d;
      g = tg * d;
      b = tb * d;
    } else if (def.paintOver) {
      const lum = (0.2126 * LINEAR[data[i]] + 0.7152 * LINEAR[data[i + 1]] + 0.0722 * LINEAR[data[i + 2]]) / meanLum;
      const m = 1 + (lum - 1) * detail;
      r = tr * m;
      g = tg * m;
      b = tb * m;
    } else {
      r = LINEAR[data[i]] * ratio[0];
      g = LINEAR[data[i + 1]] * ratio[1];
      b = LINEAR[data[i + 2]] * ratio[2];
    }
    out.data[i] = toByte(r);
    out.data[i + 1] = toByte(g);
    out.data[i + 2] = toByte(b);
    out.data[i + 3] = 255;
  }
  ctx.putImageData(out, 0, 0);
  return canvas.toDataURL("image/png");
}

/** The tinted tile for `option` in `color`, or null while it loads (or without a canvas). */
export function finishTileUrl(option: FinishOption, color: string): string | null {
  if (!option.thumb) return null;
  const key = `${option.id}|${option.thumb}|${color.toLowerCase()}`;
  const hit = tinted.get(key);
  if (hit) return hit;
  const src = decoded.get(option.thumb);
  if (src === undefined) {
    load(option.thumb);
    return null;
  }
  if (src === null) return null;
  const url = paint(src, option, color);
  if (!url) return null;
  if (tinted.size >= MAX_TILES) tinted.delete(tinted.keys().next().value as string);
  tinted.set(key, url);
  return url;
}

/** {@link finishTileUrl}, re-rendering when a swatch finishes decoding. */
export function useFinishTile(option: FinishOption, color: string): string | null {
  useSyncExternalStore(subscribe, () => version, () => 0);
  return finishTileUrl(option, color);
}
